import React from "react";
import { Frame, FramePanel } from "@/components/ui/frame";
import { Edit3Icon, SendIcon, Trash2Icon, SaveIcon, XIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ConfirmPopover } from "@/components/ui/confirm-popover";
import createDOMPurify from "dompurify";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import { useCreatorsStore, useInboxStore, useDashboardStore } from "@/stores";
import { Creator } from "@/types/types";
import { MinimalTiptapEditor } from "@/components/minimal-tiptap";
import AmyBadge from "@/components/ui/amy-badge";
import { cleanHtml, formatPlainText } from "@/utils/emailFormatters";

interface NextMessagePreviewProps {
  creator_id: string;
  nextMessage: string;
}

export default function NextMessagePreview({
  creator_id,
  nextMessage,
}: NextMessagePreviewProps) {
  const DOMPurify = React.useMemo(() => createDOMPurify(window), []);
  const { getToken, orgId } = useAuth();
  const { creators, setCreators } = useCreatorsStore();
  const { deleteNextMessage, updateNextMessage, sendNextMessage, fetchInbox } =
    useInboxStore();
  const { fetchTasks } = useDashboardStore();

  const [isEditing, setIsEditing] = React.useState(false);
  const [editedNextMessage, setEditedNextMessage] = React.useState(nextMessage);
  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    setEditedNextMessage(nextMessage);
  }, [nextMessage]);

  async function handleDeleteNextMessage() {
    const token = await getToken();
    if (!token) return;

    setIsLoading(true);
    toast.loading("Deleting message...", { id: "delete-message" });

    try {
      const success = await deleteNextMessage(creator_id, token);
      if (success) {
        const updatedCreators = creators.map((creator: Creator) =>
          creator._id === creator_id
            ? { ...creator, next_message: "" }
            : creator
        );
        setCreators(updatedCreators);

        if (orgId) {
          await fetchTasks(orgId, token);
        }

        toast.success("Message deleted.", { id: "delete-message" });
      } else {
        throw new Error("Failed to delete next message");
      }
    } catch (error) {
      toast.error("Error deleting message.", { id: "delete-message" });
    } finally {
      setIsLoading(false);
    }
  }

  async function handleUpdateNextMessage() {
    const token = await getToken();
    if (!token) return;

    setIsLoading(true);
    toast.loading("Updating message...", { id: "update-message" });

    try {
      const success = await updateNextMessage(
        creator_id,
        editedNextMessage,
        token
      );
      if (success) {
        const updatedCreators = creators.map((creator: Creator) =>
          creator._id === creator_id
            ? { ...creator, next_message: editedNextMessage }
            : creator
        );
        setCreators(updatedCreators);
        setIsEditing(false);
        toast.success("Message updated.", { id: "update-message" });
      } else {
        throw new Error("Failed to update next message");
      }
    } catch (error) {
      toast.error("Error updating message.", { id: "update-message" });
    } finally {
      setIsLoading(false);
    }
  }

  async function handleSendNextMessage() {
    const token = await getToken();
    if (!token) return;

    setIsLoading(true);
    toast.loading("Sending message...", { id: "send-message" });

    try {
      const success = await sendNextMessage(creator_id, token);
      if (success) {
        const updatedCreators = creators.map((creator: Creator) =>
          creator._id === creator_id
            ? { ...creator, next_message: "" }
            : creator
        );
        setCreators(updatedCreators);

        await fetchInbox(creator_id, token);

        if (orgId) {
          await fetchTasks(orgId, token);
        }

        toast.success("Message sent successfully!", { id: "send-message" });
      } else {
        throw new Error("Failed to send next message");
      }
    } catch (error) {
      toast.error("Error sending message.", { id: "send-message" });
    } finally {
      setIsLoading(false);
    }
  }

  function handleCancelEdit() {
    setEditedNextMessage(nextMessage);
    setIsEditing(false);
  }

  return (
    <div className="relative h-auto w-full">
      <Frame className="w-full">
        <FramePanel className="min-w-full p-5">
          <div className="flex flex-row items-center w-full mb-5 font-medium text-lg">
            <AmyBadge imgClassName="size-8" />
            <span className="ml-2 mt-0.5">Drafted this:</span>
          </div>
          {!isEditing ? (
            <div
              className="text-sm ml-12 mt-2 overflow-auto w-auto min-w-sm border-none shadow-none text-foreground/90 format-prose max-w-xl [&_a]:text-primary [&_a]:underline [&_a]:hover:decoration-2 [&_a]:underline-offset-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-3 [&_li]:mb-1 [&_img]:max-w-full [&_img]:h-auto [&_blockquote]:border-l-2 [&_blockquote]:border-muted-foreground/30 [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  cleanHtml(formatPlainText(editedNextMessage)),
                  {
                    ADD_ATTR: ["target", "rel"],
                  }
                ).replace(
                  /<a\b([^>]*)>/gi,
                  '<a$1 target="_blank" rel="noopener noreferrer">'
                ),
              }}
            />
          ) : (
            <MinimalTiptapEditor
              value={editedNextMessage}
              onChange={(content) =>
                setEditedNextMessage(typeof content === "string" ? content : "")
              }
              showToolBar={false}
              className="w-auto min-h-64 ml-12 mt-2 resize-none shadow-xs rounded-lg font-sans p-3 bg-background"
              placeholder="Edit message..."
            />
          )}
          <div className="w-full mt-5 flex flex-row justify-end gap-2">
            {!isEditing ? (
              <>
                <ConfirmPopover
                  title="Delete message?"
                  description="Delete this message and write a new one manually."
                  confirmLabel={<><Trash2Icon /> Delete Message</>}
                  confirmVariant="destructive"
                  onConfirm={handleDeleteNextMessage}
                  disabled={isLoading}
                >
                  <Button
                    variant="destructive-ghost"
                    className="mr-auto"
                    disabled={isLoading}
                    size="sm"
                  >
                    <Trash2Icon /> Delete
                  </Button>
                </ConfirmPopover>
                <Button
                  variant="secondary"
                  onClick={() => setIsEditing(true)}
                  disabled={isLoading}
                  size="sm"
                >
                  <Edit3Icon /> Edit
                </Button>
                <ConfirmPopover
                  title="Send message?"
                  description="Send Amy's message now. This action cannot be undone."
                  confirmLabel={<><SendIcon /> Yep, Send it!</>}
                  onConfirm={handleSendNextMessage}
                  disabled={isLoading}
                >
                  <Button size="sm" disabled={isLoading}>
                    <SendIcon /> Send
                  </Button>
                </ConfirmPopover>
              </>
            ) : (
              <>
                <Button
                  variant="secondary"
                  onClick={handleCancelEdit}
                  className="mr-auto"
                  disabled={isLoading}
                  size="sm"
                >
                  <XIcon /> Cancel
                </Button>
                <Button
                  onClick={handleUpdateNextMessage}
                  disabled={isLoading}
                  size="sm"
                >
                  <SaveIcon /> Save Changes
                </Button>
              </>
            )}
          </div>
        </FramePanel>
      </Frame>
    </div>
  );
}
